import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface CrusadeHistoryPageProps {
}

const CrusadeHistoryPage = (props: CrusadeHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Crusade Age - Chase the Fox`,
        description: "The Crusade Age of Casarok is a time after the fall of mighty empires when cultural expansion occurred in the world!",
        keywords: ["casarok", "casarok history", "crusade age", "fantasy timeline", "fantasy world history", "fantasy dark age", "fantasy world crusades"],
        imgSrc: _map_src,
        url: `world/history/crusade-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Crusade Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_pre_cataclysm.png`)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years of roughly 550 - 400 BCE.</em>
                    </p>
                    <p>
                        The Crusade Age was the dark age of Casarok in which much war and cultural expansion took place.
                        The fall of the Populis Empire during
                        the <NavLink className="world-link" to="/world/history/empirical-age">previous age</NavLink> left
                        a huge power vacuum in the world. Nations formed and fell constantly during this time, and it
                        became a time of great strife among all people of the world. Worship of
                        the <NavLink className="world-link" to="/world/religion/pantheon">gods and goddesses</NavLink> of
                        the world spiked dramatically, forming new regional religions and mythologies. Advancements in
                        science, technology, and magic were infrequent during this time. Government and law became
                        minimally impactful as well with the rise of nations formed by churches and other religious
                        institutions.
                    </p>
                    <p>
                        In the middle of the Crusade Age, an enlightenment occurred. Whether through divine
                        intervention, the gathering of the world's scholars, or a mix of both, the formation of
                        the <NavLink className="world-link" to="/world/languages/common">Casarok Common language</NavLink> occurred.
                        This event inspired the peoples of central Casarok to form the Crusades. Paladins, priests, and
                        others of the light formed parties to spread their culture through the sharing of this language
                        or through acts of war. One such war event was the Madaran Crusades in which groups
                        of <NavLink className="world-link" to="/world/bestiary/aspect/kitsune">kitsune</NavLink> roamed
                        the land. These kitsune desecrated and destroyed many shrines to the god of death, Ragnarok, in
                        the name of their matron deity and the goddess of life, Mother. These actions prompted swift
                        backlash from the pantheon and set in motion a series of vengeful events by Ragnarok, starting
                        with the Culling of the Kitsune beginning in this age.
                    </p>
                    <p>
                        <em>How does Casarok's Crusade Age differ from the dark ages on Earth?</em><br/>
                        The largest difference was the creation and spread of a language that would eventually come to
                        be used by all peoples of Casarok. The language sparked an enlightenment among the people and
                        gave them hope that the world was healing.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>548 BCE</em>: The remnants of the Populis Empire are split between western and eastern
                            halves.
                        </li>
                        <li>
                            <em>544 BCE</em>: The western half of the old Populis Empire becomes controlled by a relgion
                            centered around light-bringing deities of the pantheon.
                        </li>
                        <li>
                            <em>542 BCE</em>: The eastern half of the old Populis Empire is split among many smaller
                            nations that form new borders.
                        </li>
                        <li>
                            <em>533 BCE</em>: Scholars from around the world gather in the cultural capital of the
                            Western Populis Empire to form the Common language.
                        </li>
                        <li>
                            <em>530 BCE</em>: Crusades begin to spread Casarok Common throughout the world.
                        </li>
                        <li>
                            <em>526 BCE</em>: The Madaran Crusades begin with uprisings that cause many shrines to the
                            god of death around the world being destroyed.
                        </li>
                        <li>
                            <em>520 BCE</em>: The pantheon of Casarok punish all kitsune, stripping them of their
                            immortality from birthright and forcing it to be earned through trials instead.
                        </li>
                        <li>
                            <em>518 BCE</em>: The traitorous and
                            world-altering <NavLink className="world-link" to="/characters/blackfox">Blackfox</NavLink> is
                            born.
                        </li>
                        <li>
                            <em>512 BCE</em>: Casarok Common is spoken in pockets all throughout the world and quickly
                            spreads.
                        </li>
                        <li>
                            <em>507 BCE</em>: Blackfox fails his third trial to gain immortality. Fearing death, he
                            turns to Ragnarok to avoid his eventual fate.
                        </li>
                        <li>
                            <em>488 BCE</em>: Blackfox becomes the new champion of Ragnarok and forms the Zero-Tail
                            Covenant, aimed at punishing kitsune beyond what the arbiter of the gods, Tianlong, would
                            allow.
                        </li>
                        <li>
                            <em>469 BCE</em>: The equine paladin crusader Darius Stronghoof enters into a pact with
                            Blackfox, seeking to reclaim the life of his one true love. He unwillingly becomes the first
                            of Blackfox's officers, the Four Horsemen.
                        </li>
                        <li>
                            <em>457 BCE</em>: The Common language is in use by 50% of the world's population.
                        </li>
                        <li>
                            <em>444 BCE</em>: The Culling of the Kitsune begins, led by the Zero-Tail Covenant. They
                            specially target nine-tailed immortal kitsune.
                        </li>
                        <li>
                            <em>411 BCE</em>: The goddess of life, Mother, begins removing nine-tailed kitsune from
                            Casarok. Eventually, all kitsune are thought to be gone from the world.
                        </li>
                        <li>
                            <em>406 BCE</em>: Mother seeks retribution for the Culling of the Kitsune, but it falls on
                            deaf ears as Ragnarok claims no hand in the effort.
                        </li>
                        <li>
                            <em>401 BCE</em>: The Western Populis Empire falls at the hands of the newly formed Leonida
                            Kingdom, ushering in a new age of kingdoms and queendoms and moving the world to the feudal
                            system.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default CrusadeHistoryPage;